import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { ChipModule } from 'primeng/chip';

// Services
import { UserService } from '../../layout/service/user.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-anatomi',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    ToastModule,
    ProgressSpinnerModule,
    TagModule,
    SkeletonModule,
    ChipModule
  ],
  providers: [MessageService],
  template: `
    <div class="min-h-screen bg-gray-50 p-6">
      <div class="max-w-6xl mx-auto">
        <!-- Header -->
        <div class="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-3xl font-bold text-gray-900">Pembelajaran Anatomi</h1>
              <p class="text-gray-600 mt-2">Pelajari anatomi tubuh manusia melalui soal-soal interaktif</p>
            </div>
            <div class="flex items-center gap-3">
              <div class="bg-blue-50 px-4 py-2 rounded-lg">
                <div class="text-sm text-blue-600 font-medium">{{anatomyList.length}} Materi</div>
              </div>
              <p-button
                icon="pi pi-history"
                label="Riwayat"
                severity="secondary"
                (onClick)="goToHistory()"
              ></p-button>
            </div>
          </div>

          <!-- Search Bar -->
          <div class="mt-6">
            <div class="relative max-w-md">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="pi pi-search text-gray-400"></i>
              </div>
              <input
                type="text"
                [(ngModel)]="searchTerm"
                (input)="filterAnatomy()"
                placeholder="Cari materi anatomi..."
                class="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <div *ngIf="searchTerm" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  (click)="clearSearch()"
                  class="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div *ngFor="let item of [1,2,3,4,5,6]" class="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
            <div class="bg-gray-200 rounded-lg h-32 mb-4"></div>
            <div class="space-y-3">
              <div class="bg-gray-200 rounded h-5 w-3/4"></div>
              <div class="bg-gray-200 rounded h-4 w-full"></div>
              <div class="bg-gray-200 rounded h-4 w-2/3"></div>
              <div class="bg-gray-200 rounded h-10 w-full mt-4"></div>
            </div>
          </div>
        </div>

        <!-- Anatomy Cards -->
        <div *ngIf="!loading && filteredAnatomy.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div *ngFor="let anatomy of filteredAnatomy; let i = index"
               class="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow duration-300 overflow-hidden">

            <!-- Card Content -->
            <div class="p-6">
              <div class="flex justify-between items-start mb-4">
                <h3 class="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">
                  {{anatomy.name || anatomy.anatomy_name}}
                </h3>
                <div class="ml-4 bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-medium">
                  {{anatomy.details_count || 5}} Soal
                </div>
              </div>

              <p class="text-gray-600 text-sm line-clamp-3 mb-6 leading-relaxed">
                {{anatomy.description || anatomy.anatomy_description || 'Deskripsi tidak tersedia'}}
              </p>

              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center gap-2 text-sm text-gray-500">
                  <i class="pi pi-book text-blue-500"></i>
                  <span>Pembelajaran Interaktif</span>
                </div>
              </div>

              <p-button
                label="Mulai Belajar"
                icon="pi pi-play"
                severity="primary"
                styleClass="w-full"
                (onClick)="goToDetails(anatomy)"
              ></p-button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!loading && filteredAnatomy.length === 0" class="text-center py-20">
          <div class="bg-white rounded-xl shadow-sm border p-12 max-w-lg mx-auto">
            <i class="pi pi-search text-6xl text-gray-300 mb-6 block"></i>
            <h3 class="text-xl font-semibold text-gray-700 mb-3">
              {{searchTerm ? 'Tidak ada hasil ditemukan' : 'Belum ada materi tersedia'}}
            </h3>
            <p class="text-gray-500 mb-6">
              {{searchTerm ? 'Coba gunakan kata kunci yang berbeda' : 'Materi pembelajaran akan muncul di sini'}}
            </p>

            <div *ngIf="searchTerm">
              <p-button
                label="Reset Pencarian"
                icon="pi pi-refresh"
                severity="secondary"
                (onClick)="clearSearch()"
              ></p-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Messages -->
      <p-toast></p-toast>
    </div>
  `
})
export class Anatomi implements OnInit {
  loading: boolean = false;
  anatomyList: any[] = [];
  filteredAnatomy: any[] = [];
  searchTerm: string = '';

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadAnatomyList();
  }

  async loadAnatomyList() {
    this.loading = true;
    try {
      const response = await this.userService.getAnatomy({
        page: 1,
        limit: 100
      });

      if (response && response.status && response.data) {

        this.anatomyList = response.data.filter((item: any) =>
          item.is_active == 1
        );
        this.filteredAnatomy = [...this.anatomyList];

      } else {
        throw new Error('Gagal memuat daftar anatomi');
      }
    } catch (error) {
      console.error('Error loading anatomy list:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memuat daftar anatomi'
      });
    } finally {
      this.loading = false;
    }
  }

  filterAnatomy() {
    if (!this.searchTerm.trim()) {
      this.filteredAnatomy = [...this.anatomyList];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredAnatomy = this.anatomyList.filter(anatomy =>
        (anatomy.name || anatomy.anatomy_name || '').toLowerCase().includes(term) ||
        (anatomy.description || anatomy.anatomy_description || '').toLowerCase().includes(term)
      );
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.filterAnatomy();
  }

  goToDetails(anatomy: any) {
    const anatomyId = anatomy.id || anatomy.anatomy_id;
    this.router.navigate(['/app/osce/anatomi/details', anatomyId]);
  }

  goToHistory() {
    this.router.navigate(['/app/osce/anatomi/history']);
  }

  getRandomStudentCount(): number {
    return Math.floor(Math.random() * 150) + 50; // Random between 50-200 students
  }
}
